import React, { MouseEventHandler, useCallback, useMemo } from 'react';
import { generateArray } from '@shared/utils';
import { Button } from '@shared/UI';
import classNames from 'classnames';
import { useSteps } from '../../lib';
import './StepsHeader.scss';

interface StepsHeaderProps {
    className?: string;
    headerStepLabelClassName?: string;
    headerStepLineClassName?: string;
}

export const StepsHeader: React.FC<StepsHeaderProps> = (props) => {
    const { className, headerStepLabelClassName, headerStepLineClassName } = props;

    const { steps, stepNumber, setStepNumber, titles, allowedSteps, errorSteps, warningSteps, stepsClickable } =
        useSteps();
    const filteredTitles = titles.filter((title) => !!title);

    const onStepClick = useCallback<(currentStep: number) => MouseEventHandler>(
        (currentStep) => (event) => {
            if (~allowedSteps.indexOf(currentStep) && stepsClickable) {
                event?.stopPropagation();
                setStepNumber(currentStep);
            }
        },
        [setStepNumber, allowedSteps, stepsClickable],
    );

    const buttons = useMemo(() => {
        return (
            <>
                {steps.length > 1 ? (
                    generateArray(steps.length, (index) => {
                        const notAllowed = !~allowedSteps.indexOf(index);
                        const error = !!~errorSteps.indexOf(index);
                        const warning = !!~warningSteps.indexOf(index);
                        return (
                            <>
                                <div
                                    className={classNames(
                                        'feature-steps-header__step-header',
                                        {
                                            notAllowed,
                                        },
                                        {
                                            stepsNotClickable: !stepsClickable,
                                        },
                                    )}
                                    onClick={onStepClick(index)}
                                >
                                    <Button
                                        key={index}
                                        onClick={onStepClick(index)}
                                        className={classNames(
                                            'feature-steps-header__button',
                                            {
                                                primary: index === stepNumber,
                                            },
                                            {
                                                secondary: index !== stepNumber,
                                            },
                                            {
                                                error: !notAllowed && error,
                                            },
                                            {
                                                warning: !notAllowed && warning,
                                            },
                                            {
                                                notAllowed,
                                            },
                                        )}
                                    >
                                        {index + 1}
                                    </Button>
                                    {filteredTitles[index] && (
                                        <span
                                            className={classNames(
                                                'feature-steps-header__step-title',
                                                {
                                                    active: index === stepNumber,
                                                },
                                                {
                                                    error: !notAllowed && error,
                                                },
                                                {
                                                    warning: !notAllowed && warning,
                                                },
                                                {
                                                    notAllowed,
                                                },
                                                headerStepLabelClassName,
                                            )}
                                        >
                                            {filteredTitles[index]}
                                        </span>
                                    )}
                                </div>
                            </>
                        );
                    })
                ) : (
                    <span className={classNames('feature-steps-header__step-title', headerStepLabelClassName)}>
                        {filteredTitles[0]}
                    </span>
                )}
            </>
        );
    }, [steps, onStepClick, stepNumber, filteredTitles, allowedSteps, errorSteps, warningSteps]);

    return <div className={classNames('feature-steps-header', className)}>{buttons}</div>;
};
